import * as _ from 'lodash';
import * as Mutations from './mutations';
import { CouponsService } from '../../../common/services/pages/coupons.service';
import { SchoolService } from '../../../common/services/settings/school.service';
import { PayNowService } from '@/common/services/pay-now.service';
import { PaginationParams } from '../../../common/utils/pagination-params';
import { clearAllSelected } from '@/store/modules/page/reset';
import  priceFormat  from '@/filters/priceFormat';

export const CouponsModule = {
    state: {
        coupons: null,
        coupon: null,
        couponId: null,
        isPaynowFeature: false
    },

    mutations: {
        [Mutations.SET_COUPONS] (state, coupons) {
            state.coupons = coupons;
        },

        [Mutations.SET_COUPON] (state, coupon) {
            state.coupon = coupon;
        },

        [Mutations.SET_COUPON_ID] (state, couponId) {
            state.couponId = couponId;
        },

        [Mutations.REMOVE_COUPON] (state) {
            state.coupon = null;
        },

        [Mutations.REMOVE_COUPON_ID] (state) {
            state.couponId = null;
        },

        [Mutations.SET_PAYNOW_COUPON_FEATURE] (state, data) {
            state.isPaynowFeature = data;
        },

        [Mutations.SET_COUPON_USAGE] (state, { couponId, data }) {
            let coupon = state.coupons.find(coupon => coupon.id === couponId);
            if (!coupon) return;

            coupon = {
                ...coupon,
                uses: data
            };

            state.coupon = coupon;
        }
    },

    actions: {
        async getCoupons(context) {
            let isPaynowFeature = false;
            const { schoolId, userId } = context.getters;
            const { data } = await SchoolService.getProfileInfo(schoolId, userId);

            if (data.features && data.features["new-payments"]) { // check if paynow feature is enabled
                isPaynowFeature = data.features["new-payments"];
                context.commit(Mutations.SET_PAYNOW_COUPON_FEATURE, data.features["new-payments"]);
            }

            if (isPaynowFeature) {
                const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await PayNowService.getAllCoupons(context.getters.schoolId,
                    PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    }));
                                    const coupons = content.map(coupon => ({
                    id: coupon.stripeCoupon,
                    code: coupon.name,
                    type: coupon.couponType === 'FIXED_AMOUNT' ? 'Fixed dollar' : 'Percentage',
                    amount: coupon.amount,
                    onlyFirstTimeCustomers: false,
                    maximumAvailable: coupon.maxRedemption,
                    maximumPerUser: coupon.maxPerUser,
                    usedTimes: 0,
                    createdAt: coupon.createdAt,
                    expiryDate: coupon.expiryDate,
                    status: coupon.active ? 'ACTIVE' : 'EXPIRED',
                }));
            context.commit(Mutations.SET_COUPONS, coupons);
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback: () => {
                    return context.dispatch('getCoupons');
                }
            });
            } else {
                const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await CouponsService.getCouponsBySchool(context.getters.schoolId,
                    PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    }));
            context.commit(Mutations.SET_COUPONS, content);
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback: () => {
                    return context.dispatch('getCoupons');
                }
            });
            }
            context.dispatch('resetSelectedItems');
        },

        async previewCoupon(context, couponId) {
            context.commit(Mutations.SET_COUPON_ID, couponId);
            await context.dispatch('getCoupon');
            await context.dispatch('getCouponUsage',couponId);
            context.dispatch('openModal', {
                modalName: 'CouponPreviewModal',
                callback: () => context.dispatch('unstageCoupon')
            });
        },

        async addCoupon(context, form) {
            try {
                context.dispatch('showSpinner');
                if (context.state.isPaynowFeature) {
                    const paynowCoupon = {
                        name: form.code,
                        maxPerUser: form.maximumPerUser,
                        maxUsage: form.maximumAvailable,
                        amount: form.amount,
                        couponType: form.type === 'Fixed dollar' ? 'FIXED_AMOUNT' : 'PERCENTAGE',
                        expiryDate: form.expiryDate,
                    }
                    await PayNowService.createCoupon(context.getters.schoolId, paynowCoupon);
                } else {
                    await CouponsService.addCoupon(context.getters.schoolId, form);
                }

                await context.dispatch('getCoupons');
            } finally {
                setTimeout(() => {
                    context.dispatch('hideSpinner');
                });
            }
        },

        async updateCoupon(context, form) {
            try {
                context.dispatch('showSpinner');
                await CouponsService.updateCoupon(context.getters.schoolId, context.getters.couponId, {
                    ...form,
                    status: _.capitalize(form.status)
                });
                await context.dispatch('getCoupons');
                context.dispatch('unstageCoupon');
            } catch (e) {
                throw e;
            } finally {
                context.dispatch('hideSpinner');
            }
        },

        async deleteCoupon(context, couponId) {
            await CouponsService.deleteCoupon(context.getters.schoolId, couponId);
            clearAllSelected();
            await context.dispatch('getCoupons');
        },

        async getCoupon(context) {
            if (context.state.isPaynowFeature) {
                const coupon = context.state.coupons.find(coupon => coupon.id === context.getters.couponId);
                context.commit(Mutations.SET_COUPON, coupon);
            } else {
                const { data } = await CouponsService.getCoupon(context.getters.schoolId, context.getters.couponId);
                context.commit(Mutations.SET_COUPON, data);
            }
        },

        async prepareCouponForEditing(context, couponId) {
            context.commit(Mutations.SET_COUPON_ID, couponId);
            await context.dispatch('getCoupon');
            context.dispatch('openModal', {
                modalName: 'CouponUpdateModal',
                callback: () => context.dispatch('unstageCoupon')
            });
        },

        async getCouponUsage(context, couponId) {
            if (context.state.isPaynowFeature) {
                const coupon = context.state.coupons.find(coupon => coupon.id === context.getters.couponId);
                context.commit(Mutations.SET_COUPON_USAGE, { couponId, data: coupon });
          } else {
            const { data } = await CouponsService.getCouponUsage(context.getters.schoolId, couponId);
            context.commit(Mutations.SET_COUPON_USAGE, { couponId, data });
          }
        },

        unstageCoupon(context) {
            context.commit(Mutations.REMOVE_COUPON_ID);
            context.commit(Mutations.REMOVE_COUPON);
        },

        async deleteMultipleCoupons(context) {
            await CouponsService.deleteMultipleCoupons(context.getters.schoolId, context.getters.selected);
            await context.dispatch('getCoupons');
            context.dispatch('resetSelectedItems');
        }
    },

    getters: {
        coupons: (state, getters) =>
            state.coupons && state.coupons.map(coupon => formatCouponAmount(coupon, getters.currencySign)),

        couponId: state => state.couponId,

        coupon: (state, getters) => state.coupon && formatCouponAmount(state.coupon, getters.currencySign, false),

        isPaynowFeature: state => state.isPaynowFeature
    }
};

function formatCouponAmount(coupon, sign, signSpace = true) {
    return {
        ...coupon,
        amount: coupon.type === 'Percentage'
            ? priceFormat(coupon.amount) + (signSpace ? ' ' : '') +'%'
            : sign + (signSpace ? ' ' : '') + priceFormat(coupon.amount)
    };
}


// WEBPACK FOOTER //
// ./src/store/modules/coupons/coupons.module.js